/**
 * Turning a Better Auth failure into a sentence a host can act on.
 *
 * The auth screens call `authClient` directly, so they never pass through
 * `apiRequest` and never get its wording. This is that layer for them: one
 * place that decides what an auth failure says, so "No connection. Check your
 * network and try again." means the same thing on the way into the app as it
 * does everywhere behind it.
 *
 * Better Auth reports a failure two different ways, and they need opposite
 * treatment:
 *
 * - **The request never reached the server.** `@better-fetch/fetch` does not
 *   wrap its `fetch` call in a try/catch, and Better Auth does not enable that
 *   library's `catchAllError` option, so the engine's own rejection propagates
 *   out of `authClient.*` untouched — a `TypeError` reading "Load failed" on
 *   WebKit, "Failed to fetch" on Chromium. There is no returned error object
 *   and no status to read.
 * - **The server answered and refused.** The call resolves to
 *   `{ data: null, error: { status, message?, code? } }`.
 *
 * A code is not on its own a licence to show the message. `VALIDATION_ERROR`
 * carries one, and its message is a schema dump: "[body.email] Invalid input:
 * expected string, received undefined". So the codes whose wording was written
 * for a person are listed explicitly below, and anything unlisted falls back to
 * the house generic — unrecognised fails closed rather than leaking whatever
 * the service happened to say. A listed code reads from the catalog rather
 * than the wire, whose English is the service's own wording, so every sentence
 * on a sign-in screen is in the host's language.
 */

import { interfaceTranslator } from "@/i18n/translate";
import { genericMessage, networkUnreachableMessage } from "@/lib/api/api-error";

/**
 * The shape `authClient` resolves to on a refusal. Structural rather than
 * imported: Better Auth's own error type is generated per plugin set, and this
 * is the subset every one of them has in common.
 */
interface AuthErrorLike {
  code?: string;
  message?: string;
  status?: number;
  statusText?: string;
}

/**
 * Codes whose message Better Auth wrote for the person reading it, rather than
 * for whoever is reading the logs. Everything absent from this list — the
 * `FAILED_TO_*` internals, `VALIDATION_ERROR`'s schema dump, the callback-URL
 * and origin misconfigurations, `CROSS_SITE_NAVIGATION_LOGIN_BLOCKED`'s "this
 * request appears to be a CSRF attack" — reaches the reader as
 * {@link genericMessage}.
 *
 * Listing the safe ones rather than blocking the unsafe ones is deliberate: a
 * code added by a future Better Auth release is unknown copy, and unknown copy
 * should degrade to the generic sentence, not appear on a sign-in screen
 * unread. A screen that has better wording of its own passes it as
 * `copyByCode`, which also admits a code this list does not carry.
 */
const PERSON_MEANINGFUL_CODES = {
  ACCOUNT_NOT_FOUND: "accountNotFound",
  CHANGE_EMAIL_DISABLED: "changeEmailDisabled",
  CREDENTIAL_ACCOUNT_NOT_FOUND: "credentialAccountNotFound",
  EMAIL_ALREADY_VERIFIED: "emailAlreadyVerified",
  EMAIL_CAN_NOT_BE_UPDATED: "emailCanNotBeUpdated",
  EMAIL_MISMATCH: "emailMismatch",
  // Safe only while `requireEmailVerification` stays off
  // (apps/api/src/utils/auth.ts): with it on, better-auth answers sign-in with
  // this code, which would tell anyone who asked that the address has an
  // account. Turning it on means dropping this entry.
  EMAIL_NOT_VERIFIED: "emailNotVerified",
  FAILED_TO_UNLINK_LAST_ACCOUNT: "failedToUnlinkLastAccount",
  INVALID_EMAIL: "invalidEmail",
  INVALID_EMAIL_OR_PASSWORD: "invalidEmailOrPassword",
  INVALID_OTP: "invalidOtp",
  INVALID_PASSWORD: "invalidPassword",
  INVALID_TOKEN: "invalidToken",
  LINKED_ACCOUNT_ALREADY_EXISTS: "linkedAccountAlreadyExists",
  OTP_EXPIRED: "otpExpired",
  PASSWORD_ALREADY_SET: "passwordAlreadySet",
  PASSWORD_TOO_LONG: "passwordTooLong",
  PASSWORD_TOO_SHORT: "passwordTooShort",
  SESSION_EXPIRED: "sessionExpired",
  SESSION_NOT_FRESH: "sessionNotFresh",
  SOCIAL_ACCOUNT_ALREADY_LINKED: "socialAccountAlreadyLinked",
  TOKEN_EXPIRED: "tokenExpired",
  TOO_MANY_ATTEMPTS: "tooManyAttempts",
  USER_ALREADY_EXISTS: "userAlreadyExists",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "userAlreadyExistsUseAnotherEmail",
  USER_ALREADY_HAS_PASSWORD: "userAlreadyHasPassword",
  USER_NOT_FOUND: "userNotFound",
} as const satisfies Record<string, string>;

type PersonMeaningfulCode = keyof typeof PERSON_MEANINGFUL_CODES;

function isPersonMeaningfulCode(code: string): code is PersonMeaningfulCode {
  return Object.hasOwn(PERSON_MEANINGFUL_CODES, code);
}

/**
 * Better Auth's rate limiter answers 429 with a message and NO code
 * (`dist/api/rate-limiter/index.mjs`), so the allowlist above cannot admit it
 * and it would otherwise reach the reader as the house generic — an invitation
 * to retry, aimed at the one person whose retries are the problem. The limiter
 * is on whenever `NODE_ENV=production` with a 3-request rule on the sign-in,
 * sign-up, change-password and verification routes, so a host who mistypes
 * their password three times lands here.
 *
 * Our wording rather than the service's: it is a fixed string either way, and
 * this one cannot drift into jargon on a version bump. The retry window rides
 * on an `X-Retry-After` header the client error does not carry, so it says
 * "a moment" rather than inventing a number.
 */
const TOO_MANY_REQUESTS = 429;

/**
 * What each engine's `fetch` says when the request never completed. Matched on
 * the text because that is all there is: every one of these is a plain
 * `TypeError` with no code, no status and no distinguishing property.
 *
 * Matching the phrase rather than the type is what keeps the blame honest. Our
 * own bugs throw `TypeError` too ("x is not a function"), and telling a host to
 * check their network when the real fault is this app's would send them to
 * their router over a line of our code.
 */
const FETCH_REJECTION_PHRASES: readonly string[] = [
  // WebKit / Safari / iOS — the one this is named for.
  "load failed",
  // Chromium.
  "failed to fetch",
  // Firefox.
  "networkerror when attempting to fetch resource",
  // undici (Node, and anything running the suite outside a browser).
  "fetch failed",
  // Capacitor's native HTTP bridge and React Native's polyfill.
  "network request failed",
  // Reported by some WebViews when the radio is off outright.
  "the internet connection appears to be offline",
];

/** Whether this thrown value is `fetch` saying the request never completed. */
function isFetchRejection(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return FETCH_REJECTION_PHRASES.some((phrase) => message.includes(phrase));
}

/** Whether this value is a resolved `{ error }` payload rather than a throw. */
function asAuthError(error: unknown): AuthErrorLike | null {
  if (typeof error !== "object" || error === null || error instanceof Error) {
    return null;
  }
  const { code, message, status } = error as AuthErrorLike;
  const hasAuthErrorShape =
    typeof code === "string" ||
    typeof message === "string" ||
    typeof status === "number";
  return hasAuthErrorShape ? (error as AuthErrorLike) : null;
}

/**
 * The sentence to put on screen for a Better Auth failure.
 *
 * Pass either half of the pair: the `error` a call resolved with, or the value
 * its promise rejected with. Both arrive here, because both reach the reader.
 *
 * `copyByCode` is for a screen that has better wording than the service does —
 * "That verification link has expired. Send a new one." beats "Token expired"
 * on the screen whose whole subject is that link. An entry there wins over the
 * service's message and admits its code whether or not
 * {@link PERSON_MEANINGFUL_CODES} carries it.
 *
 * `fallback` replaces {@link genericMessage} for a surface that can name the
 * action that failed — "Could not remove that sign-in method." says more than
 * the house sentence and is still ours, not the service's. It is only ever
 * reached once the message on the wire has been rejected, so passing one
 * cannot put raw text on screen.
 */
export function authErrorMessage(
  error: unknown,
  copyByCode?: Readonly<Record<string, string>>,
  fallback?: string
): string {
  if (isFetchRejection(error)) {
    return networkUnreachableMessage();
  }

  const houseFallback = fallback?.trim() || genericMessage();

  const authError = asAuthError(error);
  if (!authError) {
    // A thrown Error that is not a fetch rejection is one of our own faults, or
    // a plugin's. Its message is written for a stack trace, so it stays there.
    return houseFallback;
  }

  const { code, status } = authError;

  if (status === TOO_MANY_REQUESTS) {
    return interfaceTranslator("auth.errors")("tooManyRequests");
  }

  // A code is positive evidence that the server answered: it is the service
  // naming what it refused. Read it before the status, so a refusal reaches the
  // reader as itself even where no status rode along with it.
  if (code) {
    const houseCopy =
      copyByCode && Object.hasOwn(copyByCode, code)
        ? copyByCode[code]
        : undefined;
    if (houseCopy) {
      return houseCopy;
    }
    if (isPersonMeaningfulCode(code)) {
      return interfaceTranslator("auth.errors")(PERSON_MEANINGFUL_CODES[code]);
    }
    return houseFallback;
  }

  // Defensive: no path measured on Better Auth 1.7.2 returns a statusless or
  // zero-status error — a failure to reach the server throws instead. Kept
  // because `NETWORK_UNREACHABLE` is the house marker for exactly this, and a
  // client that starts normalising the rejection would otherwise land on the
  // generic sentence while meaning the network one.
  const neverReachedServer = status === undefined || status === 0;
  if (neverReachedServer) {
    return networkUnreachableMessage();
  }

  return houseFallback;
}
